import React, { useState, useEffect, useRef } from 'react';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Statistics = ({ apiBaseUrl }) => {
  const pieChartRef = useRef(null);
  const barChartRef = useRef(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [yearlyData, setYearlyData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Generate years (current year and 2 years back)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 3 }, (_, i) => currentYear - i);
  
  // Generate months
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  // Fetch monthly statistics
  const fetchMonthlyStats = async (year, month) => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/stats/${year}/${month}`);
      const result = await response.json();
      
      if (result.success) {
        setMonthlyStats(result.data);
      }
    } catch (err) {
      console.error('月別統計の取得に失敗しました:', err);
    }
  };

  // Fetch yearly data
  const fetchYearlyData = async (year) => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/stats/${year}`);
      const result = await response.json();
      
      if (result.success) {
        setYearlyData(result.data);
      }
    } catch (err) {
      console.error('年別データの取得に失敗しました:', err);
    }
  };

  // Fetch data on mount and when year/month changes
  useEffect(() => {
    const fetchData = async () => {
      if (!apiBaseUrl) return;
      
      setLoading(true);
      await Promise.all([
        fetchMonthlyStats(selectedYear, selectedMonth),
        fetchYearlyData(selectedYear)
      ]);
      setLoading(false);
    };

    fetchData();
  }, [apiBaseUrl, selectedYear, selectedMonth]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY'
    }).format(amount);
  };

  // Pie chart data (category breakdown)
  const pieChartData = {
    labels: monthlyStats?.categoryBreakdown?.map(item => item.category) || [],
    datasets: [
      {
        data: monthlyStats?.categoryBreakdown?.map(item => item.total) || [],
        backgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
          '#FF9F40',
          '#FF6384',
          '#C9CBCF',
          '#4BC0C0'
        ],
        hoverBackgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
          '#FF9F40',
          '#FF6384',
          '#C9CBCF',
          '#4BC0C0'
        ]
      }
    ]
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = formatCurrency(context.raw);
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.raw / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  // Bar chart data (monthly income/expense)
  const barChartData = {
    labels: months.map(month => `${month}月`),
    datasets: [
      {
        label: '収入',
        data: months.map(month => {
          const monthStr = month.toString().padStart(2, '0');
          return yearlyData?.[monthStr]?.income || 0;
        }),
        backgroundColor: '#28a745',
        borderColor: '#28a745',
        borderWidth: 1
      },
      {
        label: '支出',
        data: months.map(month => {
          const monthStr = month.toString().padStart(2, '0');
          return yearlyData?.[monthStr]?.expense || 0;
        }),
        backgroundColor: '#dc3545',
        borderColor: '#dc3545',
        borderWidth: 1
      }
    ]
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${formatCurrency(context.raw)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return formatCurrency(value);
          }
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container">
      <h1>統計</h1>
      
      {/* Date Selection */}
      <div className="card">
        <div className="form-row">
          <div className="form-group">
            <label>年</label>
            <select 
              value={selectedYear} 
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {years.map(year => (
                <option key={year} value={year}>{year}年</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>月</label>
            <select 
              value={selectedMonth} 
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            >
              {months.map(month => (
                <option key={month} value={month}>{month}月</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Monthly Summary */}
      <div className="card">
        <h2>
          {selectedYear}年{selectedMonth}月の収支
        </h2>
        <div className="stats-grid">
          <div className="stat-card income">
            <div className="stat-value">
              {formatCurrency(monthlyStats?.income || 0)}
            </div>
            <div className="stat-label">収入</div>
          </div>
          
          <div className="stat-card expense">
            <div className="stat-value">
              {formatCurrency(monthlyStats?.expense || 0)}
            </div>
            <div className="stat-label">支出</div>
          </div>
          
          <div className="stat-card">
            <div className={`stat-value ${(monthlyStats?.balance || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
              {formatCurrency(monthlyStats?.balance || 0)}
            </div>
            <div className="stat-label">収支</div>
          </div>
          
          <div className="stat-card engel">
            <div className="stat-value">
              {monthlyStats?.engelCoefficient || 0}%
            </div>
            <div className="stat-label">エンゲル係数</div>
          </div>
        </div>
      </div>

      {/* Category Breakdown (Pie Chart) */}
      {monthlyStats?.categoryBreakdown?.length > 0 && (
        <div className="chart-wrapper">
          <h3 className="chart-title">
            {selectedYear}年{selectedMonth}月 カテゴリー別支出
          </h3>
          <div className="chart-container">
            <Pie 
              ref={pieChartRef}
              data={pieChartData} 
              options={pieChartOptions}
              key={`pie-${selectedYear}-${selectedMonth}`}
            />
          </div>
        </div>
      )}

      {/* Monthly Trends (Bar Chart) */}
      <div className="chart-wrapper">
        <h3 className="chart-title">
          {selectedYear}年 月別収支
        </h3>
        <div className="chart-container">
          <Bar 
            ref={barChartRef}
            data={barChartData} 
            options={barChartOptions}
            key={`bar-${selectedYear}`}
          />
        </div>
      </div>

      {/* Engel Coefficient Info */}
      <div className="card">
        <h3>エンゲル係数について</h3>
        <p style={{ color: '#666', lineHeight: '1.6' }}>
          エンゲル係数は家計の支出に占める食費の割合を示す指標です。
          一般的に25%以下が理想とされています。
        </p>
        <div style={{ marginTop: '1rem' }}>
          <div style={{ marginBottom: '0.5rem' }}>
            <strong>評価基準:</strong>
          </div>
          <ul style={{ marginLeft: '1rem', color: '#666' }}>
            <li>25%以下: 非常に良好</li>
            <li>25-30%: 良好</li>
            <li>30-35%: 普通</li>
            <li>35%以上: 改善の余地あり</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Statistics;